import { api } from "./api";

export type OrderStatus =
  | "PENDING"
  | "CONFIRMED"
  | "IN_PREPARATION"
  | "READY"
  | "IN_DELIVERY"
  | "DELIVERED"
  | "CANCELLED";

export type ServiceType = "CATERING" | "MEAL_DELIVERY";
export type Recurrence = "ONCE" | "DAILY" | "WEEKLY" | "MONTHLY";

export interface OrderItem {
  id?: string;
  productId: string;
  product?: {
    id: string;
    name: string;
    price: number;
    description?: string;
  };
  quantity: number;
  unitPrice: number;
  subtotal: number;
  isSwitch?: boolean;
  options?: string;
  notes?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerAddressId?: string | null;
  customer?: {
    id: string;
    firstName?: string | null;
    lastName: string;
    type: string;
    phone?: string;
    email?: string;
    address?: string | null;
    city?: string | null;
    postalCode?: string | null;
  };
  customerAddress?: {
    id: string;
    address: string;
    postalCode?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    zone?: { id: string; name: string };
  } | null;
  createdByUserId: string;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  orderDate: string; // Format YYYY-MM-DD
  deliveryDate: string; // Format YYYY-MM-DD
  deliveryTimeSlot?: string;
  status: OrderStatus;
  serviceType: ServiceType;
  specialInstructions?: string;
  recurrence: Recurrence;
  paymentMethodId?: string;
  paymentMethod?: {
    id: string;
    name: string;
    code?: string | null;
  };
  paymentStatusId?: string;
  paymentStatus?: {
    id: string;
    name: string;
    colorCode: string;
  };
  totalAmount: number; // TTC
  totalAmountHt?: number | null; // Montant HT (après réduction)
  vatAmount?: number | null; // Montant TVA
  vatRate?: number | null; // Taux TVA appliqué (ex: 0.085)
  discountAmount?: number | null;
  deliveryCost?: number | null;
  items: OrderItem[];
  createdAt: string;
  updatedAt: string;
  delivery?: {
    id: string;
    driverId: string | null;
    zoneId: string;
    deliveryDate: string;
    status: string;
    scheduledDeliveryTime: string | null;
    actualDeliveryTime: string | null;
    driver?: { id: string; firstName: string; lastName: string; phone?: string };
    zone?: { id: string; name: string; zoneNumber: number };
  } | null;
  statusHistory?: Array<{
    id: string;
    status: OrderStatus;
    createdAt: string;
  }>;
}

export interface CreateOrderData {
  customerId: string;
  customerAddressId?: string | null; // Optionnel : adresse de livraison (défaut = adresse par défaut du client)
  orderDate?: string; // Format YYYY-MM-DD, défaut aujourd'hui
  deliveryDate: string; // Format YYYY-MM-DD
  deliveryTimeSlot?: string;
  serviceType: ServiceType;
  recurrence?: Recurrence;
  paymentMethodId?: string;
  paymentStatusId?: string;
  specialInstructions?: string;
  discountAmount?: number | null;
  deliveryCost?: number | null;
  items: {
    productId: string;
    quantity: number;
    isSwitch?: boolean;
    options?: string;
    notes?: string;
  }[];
}

export interface UpdateOrderData {
  customerId?: string;
  customerAddressId?: string;
  deliveryDate?: string;
  deliveryTimeSlot?: string;
  status?: OrderStatus;
  serviceType?: ServiceType;
  recurrence?: Recurrence;
  paymentMethodId?: string;
  paymentStatusId?: string;
  specialInstructions?: string;
  discountAmount?: number | null;
  deliveryCost?: number | null;
  items?: {
    productId: string;
    quantity: number;
    isSwitch?: boolean;
    options?: string;
    notes?: string;
  }[];
}

export type ProductTypeFilter =
  | "STARTER"
  | "MAIN_COURSE"
  | "DESSERT"
  | "DRINK"
  | "SIDE";

export interface OrderFilter {
  search?: string;
  status?: OrderStatus;
  customerId?: string;
  zoneId?: string;
  deliveryDateFrom?: string; // Format YYYY-MM-DD
  deliveryDateTo?: string; // Format YYYY-MM-DD
  serviceType?: ServiceType;
  paymentStatusId?: string;
  productType?: ProductTypeFilter;
  productId?: string;
  page?: number;
  limit?: number;
}

export interface OrdersPaginatedResponse {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
}

/** Produit présent dans au moins une commande sur la période (pour le filtre) */
export interface ProductInPeriod {
  id: string;
  name: string;
  type: ProductTypeFilter;
}

/**
 * Service pour la gestion des commandes
 */
export const orderService = {
  /**
   * Récupère les commandes avec filtres. Pagination optionnelle :
   * si page/limit fournis, retourne { orders, total, page, limit } ;
   * sinon retourne toutes les commandes (orders uniquement, pour Dashboard etc.).
   */
  async getAll(
    filters?: OrderFilter,
  ): Promise<OrdersPaginatedResponse | Order[]> {
    const params: Record<string, string | number | undefined> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.customerId) params.customerId = filters.customerId;
    if (filters?.zoneId) params.zoneId = filters.zoneId;
    if (filters?.deliveryDateFrom) params.deliveryDateFrom = filters.deliveryDateFrom;
    if (filters?.deliveryDateTo) params.deliveryDateTo = filters.deliveryDateTo;
    if (filters?.serviceType) params.serviceType = filters.serviceType;
    if (filters?.paymentStatusId) params.paymentStatusId = filters.paymentStatusId;
    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.productType) params.productType = filters.productType;
    // Envoyer productId explicitement pour le filtre par produit (évite que la liste ne se mette pas à jour)
    if (filters?.productId && String(filters.productId).trim()) {
      params.productId = String(filters.productId).trim();
    }
    if (filters?.page != null) params.page = filters.page;
    if (filters?.limit != null) params.limit = filters.limit;

    const response = await api.get<{
      success: boolean;
      data?: Order[];
      total?: number;
      page?: number;
      limit?: number;
    }>("/orders", { params });
    const payload = response.data;
    const orders = Array.isArray(payload.data) ? payload.data : [];

    const isPaginatedRequest =
      filters?.page != null && filters?.limit != null;

    if (isPaginatedRequest && filters) {
      const page = filters.page!;
      const limit = filters.limit!;
      return {
        orders,
        total: payload.total ?? orders.length,
        page: payload.page ?? page,
        limit: payload.limit ?? limit,
      };
    }
    return orders;
  },

  /**
   * Liste des produits présents dans au moins une commande sur la période sélectionnée.
   * Utilisé pour le filtre Produit (liste = produits proposés sur la période).
   */
  async getProductsInPeriod(
    deliveryDateFrom: string,
    deliveryDateTo: string,
    productType?: ProductTypeFilter
  ): Promise<ProductInPeriod[]> {
    const params: Record<string, string> = {
      deliveryDateFrom,
      deliveryDateTo,
    };
    if (productType) params.productType = productType;

    const response = await api.get<{ success: boolean; data: ProductInPeriod[] }>(
      "/orders/products-in-period",
      { params }
    );
    return response.data?.data ?? [];
  },

  /**
   * Récupère une commande par son ID
   */
  async getById(id: string): Promise<Order> {
    const response = await api.get<{ success: boolean; data: Order }>(
      `/orders/${id}`,
    );
    return response.data.data;
  },

  /**
   * Crée une nouvelle commande
   */
  async create(data: CreateOrderData): Promise<Order> {
    const response = await api.post<{ success: boolean; data: Order }>(
      "/orders",
      data,
    );
    return response.data.data;
  },

  /**
   * Met à jour une commande
   */
  async update(id: string, data: UpdateOrderData): Promise<Order> {
    const response = await api.put<{ success: boolean; data: Order }>(
      `/orders/${id}`,
      data,
    );
    return response.data.data;
  },

  /**
   * Annule une commande
   */
  async cancel(id: string): Promise<Order> {
    const response = await api.patch<{ success: boolean; data: Order }>(
      `/orders/${id}/cancel`,
    );
    return response.data.data;
  },

  /**
   * Supprime une commande (soft delete si possible)
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/orders/${id}`);
  },
};
